import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { httpRequestsOnSuccessActions, sessionActions } from 'store';
import { FindLoad, FindSuccessItem, renderTargetStatuses, useModal } from 'utils';
import { CustomDelete, NotError } from 'components';
import { findEntryProblem, hasEntryChanges } from '../../manualSession/constants';
import { buildCorrectionChanges, changedStatuses, EDIT_SESSION_DATA_ACTION, sessionGoalList, sessionTrialsByTarget } from './constants';

const statusLabel = (status) => renderTargetStatuses?.find((s) => s?.value === status)?.label || status;

/**
 * Edit-mode state of the session screen: the draft trials, Save validation and the batch commit.
 * The draft is seeded once, when edit mode starts, so a session refetch while editing never wipes
 * what the user typed.
 */
export const useEditSessionData = (session) => {
    const dispatch = useDispatch();
    const { open, close } = useModal();
    const saving = FindLoad(EDIT_SESSION_DATA_ACTION);
    const success = FindSuccessItem(EDIT_SESSION_DATA_ACTION);

    const [editing, setEditing] = useState(false);
    const [entries, setEntries] = useState({});
    const [initial, setInitial] = useState({});
    // `{ targetId, trialIndex }` of the trial Save rejected — that trial's inputs show the error.
    const [entryError, setEntryError] = useState(null);

    const dirty = editing && hasEntryChanges(entries, initial);

    const exit = () => {
        setEditing(false);
        setEntries({});
        setInitial({});
        setEntryError(null);
    };

    const start = () => {
        const seeded = sessionTrialsByTarget(session);
        setEntries(seeded);
        setInitial(seeded);
        setEntryError(null);
        setEditing(true);
    };

    useEffect(() => {
        if (!success) return;
        dispatch(httpRequestsOnSuccessActions.removeSuccess(EDIT_SESSION_DATA_ACTION));
        exit();

        const moved = changedStatuses(success.data);
        toast(
            <NotError
                errMessage={
                    <>
                        Session data updated. Mastery has been recalculated.
                        {moved.map((t) => (
                            <span key={t.targetId} style={{ display: 'block', marginTop: 4 }}>
                                {`${t.targetName}: ${statusLabel(t.statusBefore)} → ${statusLabel(t.statusAfter)}`}
                            </span>
                        ))}
                    </>
                }
            />
        );
    }, [success]);

    // A reload or tab close would lose the draft — let the browser ask.
    useEffect(() => {
        if (!dirty) return undefined;
        const warn = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    /** Runs `then` right away when nothing changed, otherwise after the user confirms discarding the draft. */
    const confirmDiscard = (then) => {
        if (!dirty) return then();
        open(
            <CustomDelete
                info="Discard Changes?"
                text="The changes you made to this session's data will be lost."
                innerText="Discard Changes"
                handleClose={close}
                handleDel={() => {
                    close();
                    then();
                }}
            />
        );
    };

    const cancel = () => confirmDiscard(exit);

    const save = () => {
        const problem = findEntryProblem(sessionGoalList(session), entries);
        if (problem) {
            setEntryError({ targetId: problem.targetId, trialIndex: problem.trialIndex });
            // A missing input marked red says what's wrong; the toast covers problems with no field to mark or a wrong value.
            if (!problem.fields.length || problem.explain) toast(<NotError type="error" errMessage={problem.message} />);
            (
                document.getElementById(`manual-target-${problem.targetId}`) || document.getElementById(`manual-pinned-${problem.targetId}`)
            )?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
            return;
        }
        setEntryError(null);

        const changes = buildCorrectionChanges(session, entries, initial);
        if (!changes.length) return exit();
        dispatch(sessionActions.applySessionDataCorrections(session?.id, { changes }));
    };

    const setTargetTrials = (targetId, trials) => {
        if (targetId === entryError?.targetId) setEntryError(null);
        setEntries((prev) => ({ ...prev, [targetId]: trials }));
    };

    return { editing, start, cancel, save, confirmDiscard, dirty, saving: !!saving?.length, entries, entryError, setTargetTrials };
};
